import axios from 'axios';

// Base URL for the Node proctoring endpoints. The global axios interceptor in
// App.js adds the Bearer token, so these helpers only build the request.
const trimSlash = (value) => String(value).trim().replace(/\/+$/, '');

export const resolveProctoringApi = () => {
  const direct = process.env.REACT_APP_PROCTORING_API;
  if (direct && direct.trim()) return trimSlash(direct);
  const profiles = process.env.REACT_APP_GET_CANDIDATE_PROFILES_API;
  if (profiles && profiles.trim()) {
    try {
      return `${new URL(profiles.trim()).origin}/api/proctoring`;
    } catch (err) {
      // Not an absolute URL; fall through to the relative default.
    }
  }
  return '/api/proctoring';
};

export const PROCTORING_API = resolveProctoringApi();

const unwrap = (res, fallback) => {
  const data = res && res.data ? res.data : {};
  if (data.success === false) throw new Error(data.message || fallback);
  return data;
};

// GET /sessions?limit&skip&status&round&q -> { total, sessions }
export const fetchProctoringSessions = async (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  });
  const qs = query.toString();
  const res = await axios.get(`${PROCTORING_API}/sessions${qs ? `?${qs}` : ''}`);
  const data = unwrap(res, 'Failed to load sessions');
  const sessions = Array.isArray(data.sessions) ? data.sessions : [];
  return { total: Number.isFinite(data.total) ? data.total : sessions.length, sessions };
};

// GET /candidates/:candidateId -> { candidate, sessions }
export const fetchProctoringCandidate = async (candidateId) => {
  const res = await axios.get(`${PROCTORING_API}/candidates/${encodeURIComponent(candidateId)}`);
  const data = unwrap(res, 'Failed to load candidate');
  return {
    candidate: data.candidate || null,
    sessions: Array.isArray(data.sessions) ? data.sessions : [],
  };
};

// GET /snapshots/:snapshotId -> Blob (JPEG bytes of the camera image kept for one episode).
// The token travels in a header, so an <img> cannot load this URL itself: callers
// turn the blob into an object URL and revoke it when they are done.
export const fetchProctoringSnapshot = async (snapshotId, { signal } = {}) => {
  const res = await axios.get(`${PROCTORING_API}/snapshots/${encodeURIComponent(snapshotId)}`, {
    responseType: 'blob',
    signal,
  });
  const blob = res ? res.data : null;
  // A proxy that answers an unknown path with a 200 HTML page must not pass as an image.
  if (!blob || !blob.size || !String(blob.type || '').startsWith('image/')) {
    throw new Error('Snapshot is not an image');
  }
  return blob;
};

export const isNotFound = (err) => Boolean(err && err.response && err.response.status === 404);

export const describeApiError = (err, fallback = 'That did not work') => {
  const status = err && err.response ? err.response.status : null;
  if (status === 401) return `${fallback}. Your session has expired. Sign in again.`;
  if (status === 403) return `${fallback}. Your account does not have access to camera proctoring.`;
  if (err && err.request && !err.response) return `${fallback}. The server could not be reached. Check your connection and try again.`;
  // A blob response (snapshot) carries no parsed message.
  const data = err && err.response ? err.response.data : null;
  const serverMsg = data && typeof data === 'object' && typeof data.message === 'string' ? data.message : '';
  const msg = serverMsg || (err && err.message) || '';
  return msg ? `${fallback}. ${msg}` : `${fallback}. Try again.`;
};
